// Helpers to update an order's payment status from the success / error / result
// landing pages — runs entirely in the browser against Firestore.

import { collection, doc, getDocs, query, where, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/config";

/**
 * Locate the Firestore order document by its tracking_no.
 * Returns { id, ref, data } or null.
 */
export async function findOrderByTrackingNo(trackingNo) {
  if (!trackingNo) return null;
  const snap = await getDocs(query(collection(db, "orders"), where("tracking_no", "==", trackingNo)));
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ref: d.ref, data: d.data() };
}

/**
 * Mark an order as paid/placed.
 * Idempotent — no-op if the order is already placed.
 */
export async function markOrderPlaced(trackingNo, paymentInfo = {}) {
  const found = await findOrderByTrackingNo(trackingNo);
  if (!found) return null;
  if (found.data.status && found.data.status !== "awaiting_payment") {
    return found; // already processed
  }
  const history = Array.isArray(found.data.status_history) ? found.data.status_history : [];
  await updateDoc(found.ref, {
    status: "placed",
    paid_at: serverTimestamp(),
    payment: { provider: "epoint", ...paymentInfo },
    status_history: [...history, { status: "placed", ts: new Date().toISOString() }],
    updated_at: serverTimestamp(),
  });
  return found;
}

/**
 * Mark an order as cancelled (payment failed / declined / cancelled).
 * Idempotent.
 */
export async function markOrderCancelled(trackingNo, paymentInfo = {}) {
  const found = await findOrderByTrackingNo(trackingNo);
  if (!found) return null;
  if (found.data.status && found.data.status !== "awaiting_payment") {
    return found;
  }
  const history = Array.isArray(found.data.status_history) ? found.data.status_history : [];
  // Use a dedicated `payment_failed` status (NOT `cancelled`) so the order is
  // hidden from both the customer's "Sifarişlərim" page and the admin orders
  // panel — neither view should show orders where the bank never charged the
  // card. Admin-initiated cancellations of paid orders still use `cancelled`.
  await updateDoc(found.ref, {
    status: "payment_failed",
    payment: { provider: "epoint", ...paymentInfo },
    status_history: [...history, { status: "payment_failed", ts: new Date().toISOString() }],
    updated_at: serverTimestamp(),
  });
  return found;
}
